import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import AdvancedQuestionnaire from './AdvancedQuestionnaire';
import {
  AdvancedReportStatus, downloadAdvancedReport, getAdvancedReportStatus,
  requestAdvancedRecovery, submitAdvancedAssessment,
} from './advancedAssessmentApi';

const Workspace = styled.section`
  max-width: 58rem; margin: 0 auto; padding: 2rem 1rem;
  color: #143562; background: white;
  h1 { font-size: clamp(1.8rem, 4vw, 2.6rem); line-height: 1.2; }
  p { line-height: 1.6; }
  button, a.action {
    display: inline-block; padding: .8rem 1.2rem; margin: .5rem .5rem .5rem 0;
    min-height: 44px; border: 1px solid #0A7DAA; background: #0A7DAA;
    color: white; font: inherit; cursor: pointer; text-decoration: none;
  }
  button:disabled { opacity: .6; cursor: wait; }
  button:focus-visible, a:focus-visible { outline: 3px solid #143562; outline-offset: 3px; }
  .support { border-top: 1px solid #ddd; margin-top: 2rem; padding-top: 1rem; font-size: .9rem; }
  details { padding: .75rem 0; border-top: 1px solid #ddd; }
  dt { font-weight: 600; margin-top: 1rem; }
  dd { margin: .3rem 0 1rem; white-space: pre-wrap; overflow-wrap: anywhere; }
`;

interface Props { sessionId: string; accessToken: string; email: string; organization: string; }

const AdvancedAssessmentWorkspace: React.FC<Props> = ({ sessionId, accessToken, email, organization }) => {
  const [report, setReport] = useState<AdvancedReportStatus | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState('');
  const [recoveryUntil, setRecoveryUntil] = useState(0);

  useEffect(() => {
    if (!recoveryUntil) return;
    const timer = setTimeout(() => setRecoveryUntil(0), Math.max(0, recoveryUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [recoveryUntil]);

  const refresh = useCallback(async () => {
    const status = await getAdvancedReportStatus(sessionId, accessToken);
    setReport(status);
    setError('');
  }, [sessionId, accessToken]);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const poll = async () => {
      try {
        const status = await getAdvancedReportStatus(sessionId, accessToken);
        if (!active) return;
        setReport(status); setError('');
        if (!['pending', 'generating'].includes(status.state)) return;
      } catch (err) { if (active) setError((err as Error).message); }
      if (active && ++attempts < 120) timer = setTimeout(poll, 5000);
    };
    void poll();
    return () => { active = false; clearTimeout(timer); };
  }, [sessionId, accessToken, report?.state]);

  const submit = async () => {
    await submitAdvancedAssessment(sessionId, accessToken);
    setReport({ state: 'pending' });
  };
  const action = async (work: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await work(); } catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };

  return <Workspace aria-label="Your private assessment">
    <p>Private assessment · {organization}</p>
    <h1>{report?.state === 'ready' ? 'Your decision brief is ready.' : 'From your answers to a practical decision.'}</h1>
    {!report && <p>Loading your assessment…</p>}
    {report?.state === 'not_requested' && <>
      <p>Work through the questions in manageable sections. At the end you can review every answer before submitting. The report connects your business priorities with opportunities, costs, prerequisites and next steps.</p>
      <AdvancedQuestionnaire sessionId={sessionId} accessToken={accessToken} onComplete={submit} />
    </>}
    {report && ['pending', 'generating'].includes(report.state) && <div role="status">
      <h2>{report.state === 'generating' ? 'Preparing your report' : 'Your answers have been submitted'}</h2>
      <p>We are assessing your priorities, checking supporting evidence and assembling your decision brief. This can take several minutes. You do not need to keep this page open.</p>
      <p>Your answers are saved. We’ll email a private link when your report is ready. You can also return here to check progress and download it.</p>
    </div>}
    {report?.state === 'ready' && <>
      <p>Download your private report, then book the included results-review call to challenge the assumptions and agree on a useful first step.</p>
      <ol><li>Read the decision brief and priorities.</li><li>Check the evidence, cost assumptions and prerequisites.</li><li>Bring your questions to the review call before committing to implementation.</li></ol>
      <button disabled={busy || !report.formats?.includes('pdf')} onClick={() => action(() => downloadAdvancedReport(sessionId, accessToken))}>Download my report (PDF)</button>
      <a className="action" href="/book">Book my results-review call</a>
    </>}
    {report && ['failed', 'superseded'].includes(report.state) && <>
      <h2>Your report needs attention</h2>
      <p>We could not complete your report. Your answers and payment are retained. You can retry preparation or contact support with the reference below.</p>
      <button disabled={busy} onClick={() => action(submit)}>Retry report preparation</button>
    </>}
    {error && <p role="alert">{error}</p>}
    {(!report || report.state !== 'not_requested') && <button disabled={busy} onClick={() => action(refresh)}>Check report status</button>}
    <div className="support">
      <p>Need help? <a href="mailto:support@davincisolutions.ai">Contact assessment support</a>. Reference: {sessionId}</p>
      <button disabled={busy || Date.now() < recoveryUntil || !email} onClick={() => action(async () => {
        const result = await requestAdvancedRecovery({ sessionId, email });
        setRecoveryUntil(Date.now() + result.resendAvailableInSeconds * 1000);
        setRecovery('Recovery requested. If this address matches the assessment, a private link will arrive shortly.');
      })}>Email a private recovery link</button>
      {recovery && <p role="status">{recovery}</p>}
    </div>
  </Workspace>;
};

export default AdvancedAssessmentWorkspace;
